import { useEffect, useRef, useState } from 'react';
import { emit } from '@/lib/analytics';
import { ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Combobox } from '@base-ui/react/combobox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { useConnectMetaSignup } from '@/components/use-connect-meta-signup';
import { useConnectInstagramLogin } from '@/components/use-connect-instagram-login';
import { useConnectFacebookPage, type FacebookConnectStep } from '@/components/use-connect-facebook-page';
import { FacebookGlyph } from '@/components/platform-glyph';
import { FacebookPagePicker } from '@/components/facebook-page-picker';
import { TypeRow } from '@/components/type-row';
import { useWorkspace } from '@/contexts/workspace-context';
import {
  useApiMutationWithTransport,
  useApiQueryWithTransport,
} from '@/hooks/use-api-query';
import { useCurrentRole } from '@/hooks/use-current-role';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';
import { qk } from '@/lib/query/keys';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

// Instagram connect is gated by the LaunchDarkly `instagram-enabled` flag,
// evaluated server-side and relayed via GET /config — see useInstagramEnabled.
// (Replaced the hardcoded import.meta.env.DEV gate on 2026-07-08.)

/** Option from GET /organizations/:orgId/workspaces. */
interface WorkspaceOption {
  publicId: string;
  name: string;
}

const CREATE_NEW_TARGET: WorkspaceOption = {
  publicId: 'create-new',
  name: 'Create New',
};

type WorkspaceKind = 'team' | 'customer';

interface ConnectChannelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Workspace-scoped mode: the target workspace is fixed. */
  workspacePublicId?: WorkspacePublicId | null;
  /**
   * Org-scoped mode: no fixed workspace. Renders a Workspaces/Customers toggle
   * and a searchable picker; the chosen workspace becomes the connect target.
   */
  orgId?: string;
  /**
   * Org-scoped mode: connect for a customer only. Hides the Workspaces/Customers
   * toggle and locks the picker to customers (e.g. opened from the SaaS Channels
   * page, where channels always belong to a customer).
   */
  customerOnly?: boolean;
  /**
   * Org-scoped mode: which tab the picker opens on when not locked by
   * customerOnly. Callers derive it from the page the modal was opened from
   * (customer-facing pages default to 'customer') so the target matches the
   * user's context. Defaults to 'team'.
   */
  defaultKind?: WorkspaceKind;
  fetchWithAuth: FetchWithAuth;
}

export function ConnectChannelModal({
  open,
  onOpenChange,
  workspacePublicId,
  orgId,
  customerOnly = false,
  defaultKind = 'team',
  fetchWithAuth,
}: ConnectChannelModalProps) {
  const orgMode = !!orgId;
  // AIT-521 — both create paths are org-admin only server-side: POST
  // /workspaces checks the org role directly, POST /organizations/:orgId/
  // customers is gated 'team.manage' (an ADMIN_ONLY_ACTION). Offering "Create
  // New" to a member ends in a 403 after they have already named the target,
  // so the option is withheld and the reason shown up front. Least privilege
  // while the session loads: role is null, canCreateTarget is false.
  const { role: orgRole } = useCurrentRole();
  const canCreateTarget = orgRole === 'admin';
  const instagramEnabled = useInstagramEnabled();
  // AIT-621: hidden (not disabled) while the facebook-enabled flag is off, so
  // production is visually unchanged until launch.
  const facebookEnabled = useFacebookEnabled();
  const whatsAppCardRef = useRef<HTMLButtonElement>(null);
  const { refreshWorkspaces } = useWorkspace();

  // Org mode: pick the workspace kind, then a specific workspace as the target.
  // customerOnly locks the kind to 'customer' and hides the toggle.
  const initialKind = customerOnly ? 'customer' : defaultKind;
  const [kind, setKind] = useState<WorkspaceKind>(initialKind);
  const [selected, setSelected] = useState<WorkspaceOption | null>(null);
  const [createdTarget, setCreatedTarget] = useState<
    (WorkspaceOption & { kind: WorkspaceKind }) | null
  >(null);
  const [newTargetName, setNewTargetName] = useState('');
  const [newTargetExternalId, setNewTargetExternalId] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);

  // Both target lists load when the org modal opens (same org-scoped key shape
  // + endpoint as the org pages' pickers, so the cache is shared). The Customers
  // tab can then represent true absence of customer workspaces instead of "not
  // fetched yet". The transport reuses the caller's authenticated fetch.
  const orgWorkspacesEnabled = orgMode && open && !!orgId;
  const teamWorkspaces = useApiQueryWithTransport<{ workspaces: WorkspaceOption[] }>(
    qk.org(orgId ?? '', 'workspaces', 'team'),
    `/organizations/${orgId}/workspaces?kind=team`,
    fetchWithAuth,
    { enabled: orgWorkspacesEnabled },
  );
  const customerWorkspaces = useApiQueryWithTransport<{ workspaces: WorkspaceOption[] }>(
    qk.org(orgId ?? '', 'workspaces', 'customer'),
    `/organizations/${orgId}/workspaces?kind=customer`,
    fetchWithAuth,
    { enabled: orgWorkspacesEnabled },
  );
  const optionsByKind: Record<WorkspaceKind, WorkspaceOption[]> = {
    team: teamWorkspaces.data?.workspaces ?? [],
    customer: customerWorkspaces.data?.workspaces ?? [],
  };

  const createTarget = useApiMutationWithTransport<
    { id: string; name: string },
    { kind: WorkspaceKind; name: string; externalId?: string }
  >(
    fetchWithAuth,
    ({ kind: targetKind, name, externalId }) => ({
      path: targetKind === 'customer' ? `/organizations/${orgId}/customers` : '/workspaces',
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, ...(externalId ? { externalId } : {}) }),
      },
      fallback: `Failed to create ${targetKind === 'customer' ? 'customer' : 'workspace'}`,
    }),
  );

  // Clear the previous target each time the org modal opens.
  /* eslint-disable react-hooks/set-state-in-effect -- modal-session reset; must run on open transitions, not during render (same pattern as edit-profile-dialog) */
  useEffect(() => {
    if (orgMode && open) {
      setSelected(null);
      setKind(initialKind);
      setCreatedTarget(null);
      setNewTargetName('');
      setNewTargetExternalId('');
      setCreatingNew(false);
    }
  }, [orgMode, open, initialKind]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const options =
    createdTarget?.kind === kind
      ? [createdTarget, ...optionsByKind[kind].filter((ws) => ws.publicId !== createdTarget.publicId)]
      : optionsByKind[kind];
  const targetLabel = kind === 'customer' ? 'Customer' : 'Workspace';
  const newTargetNameTrimmed = newTargetName.trim();
  const createNewTarget =
    orgId && canCreateTarget && creatingNew && newTargetNameTrimmed
      ? async (): Promise<WorkspacePublicId> => {
          const targetKind = kind;
          const workspace = await createTarget.mutateAsync({
            kind: targetKind,
            name: newTargetNameTrimmed,
            ...(targetKind === 'customer' && newTargetExternalId.trim()
              ? { externalId: newTargetExternalId.trim() }
              : {}),
          });
          const option = {
            publicId: workspace.id,
            name: workspace.name,
            kind: targetKind,
          };
          setCreatedTarget(option);
          setSelected(option);
          setCreatingNew(false);
          if (targetKind === 'team') {
            emit('workspace_created', {
              workspace_id: workspace.id,
              created_at: new Date().toISOString(),
            });
          }
          void Promise.all([
            refreshWorkspaces(),
            targetKind === 'customer' ? customerWorkspaces.refetch() : teamWorkspaces.refetch(),
          ]);
          return workspace.id as WorkspacePublicId;
        }
      : undefined;
  const targetWs = orgMode
    ? ((selected?.publicId as WorkspacePublicId | undefined) ?? createNewTarget)
    : workspacePublicId;
  const handleWhatsAppClick = useConnectMetaSignup(targetWs, fetchWithAuth);
  const handleInstagramLoginClick = useConnectInstagramLogin(targetWs, fetchWithAuth);
  // The Facebook dialog popup must open synchronously on click, so the async
  // create-new target cannot resolve first: Facebook needs a concrete workspace.
  const fbTargetWs = typeof targetWs === 'string' ? targetWs : undefined;
  const facebook = useConnectFacebookPage(fbTargetWs, fetchWithAuth, {
    open,
    onRequestOpen: () => onOpenChange(true),
    onDone: () => onOpenChange(false),
  });
  // A type predicate rather than a boolean so `facebook.step` narrows to the
  // steps the picker renders.
  const isPickerStep = (step: FacebookConnectStep): step is 'loadingPages' | 'picking' | 'submitting' =>
    step !== 'idle' && step !== 'awaitingToken';
  const fbPickerStep = isPickerStep(facebook.step) ? facebook.step : null;
  const fbPickerActive = fbPickerStep !== null;

  // In org mode the channel rows stay inert until a target is selected or named.
  const needsTarget = orgMode && !selected && !createNewTarget;

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) facebook.reset();
  };

  const handleWhatsAppConnect = async () => {
    if (await handleWhatsAppClick()) onOpenChange(false);
  };

  const handleInstagramConnect = async () => {
    emit('instagram_connect_clicked', {});
    emit('instagram_login_clicked', {});
    if (await handleInstagramLoginClick()) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-label="Connect Channel"
        className="max-h-[90vh] sm:max-w-md"
        initialFocus={orgMode ? undefined : whatsAppCardRef}
      >
        <DialogHeader>
          <DialogTitle>Connect Channel</DialogTitle>
          <DialogDescription>
            {fbPickerActive
              ? 'Pick the Facebook Page to connect.'
              : !orgMode
                ? 'Pick the channel type you want to connect.'
                : customerOnly
                  ? 'Choose a customer, then pick the channel type.'
                  : 'Choose where to connect, then pick the channel type.'}
          </DialogDescription>
        </DialogHeader>

        {fbPickerStep && (
          <FacebookPagePicker step={fbPickerStep} pages={facebook.pages} onPick={(p) => void facebook.pick(p)} />
        )}

        {!fbPickerActive && orgMode && (
          <div className="flex flex-col gap-3">
            {!customerOnly && (
              <Tabs
                value={kind}
                onValueChange={(v) => {
                  const nextKind = v as WorkspaceKind;
                  setKind(nextKind);
                  setSelected(null);
                  setNewTargetName('');
                  setNewTargetExternalId('');
                  setCreatingNew(false);
                }}
              >
                <TabsList className="w-full">
                  <TabsTrigger value="team" disabled={createTarget.isPending} className="flex-1">
                    Workspaces
                  </TabsTrigger>
                  <TabsTrigger value="customer" disabled={createTarget.isPending} className="flex-1">
                    Customers
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            <WorkspacePicker
              kind={kind}
              options={options}
              selected={selected}
              canCreate={canCreateTarget}
              creatingNew={creatingNew}
              onSelect={(workspace) => {
                setSelected(workspace);
                if (workspace) {
                  setCreatingNew(false);
                  setNewTargetName('');
                  setNewTargetExternalId('');
                }
              }}
              onCreate={() => {
                setSelected(null);
                if (!creatingNew) {
                  setNewTargetName('');
                  setNewTargetExternalId('');
                  setCreatingNew(true);
                }
              }}
            />
            {!canCreateTarget ? (
              <p className="text-xs text-muted-foreground">
                Only an organization admin can add a new {targetLabel.toLowerCase()}. Pick an
                existing one, or ask an admin to add it.
              </p>
            ) : null}
            {canCreateTarget && creatingNew ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="connect-target-name">{targetLabel} Name</Label>
                  <Input
                    id="connect-target-name"
                    placeholder={kind === 'customer' ? 'Acme Cafe' : 'Acme WhatsApp'}
                    value={newTargetName}
                    onChange={(event) => setNewTargetName(event.target.value)}
                    maxLength={60}
                    autoFocus
                  />
                </div>
                {kind === 'customer' ? (
                  <div className="space-y-2">
                    <Label htmlFor="connect-target-external-id">External ID</Label>
                    <Input
                      id="connect-target-external-id"
                      placeholder="crm-123"
                      value={newTargetExternalId}
                      onChange={(event) => setNewTargetExternalId(event.target.value)}
                      maxLength={120}
                      aria-describedby="connect-target-external-id-hint"
                    />
                    <p
                      id="connect-target-external-id-hint"
                      className="text-xs text-muted-foreground"
                    >
                      Optional. A unique identifier used in your external systems.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {!fbPickerActive && (
          <div className="flex flex-col gap-2">
            <TypeRow
              name="WhatsApp"
              icon={<WhatsAppGlyph />}
              iconTintClass="bg-[#25D366]/10"
              buttonRef={whatsAppCardRef}
              disabled={needsTarget || createTarget.isPending}
              disabledHint={needsTarget || createTarget.isPending ? '' : undefined}
              onClick={() => void handleWhatsAppConnect()}
            />
            <TypeRow
              name="Instagram"
              icon={<InstagramGlyph />}
              iconTintClass="bg-gradient-to-br from-[#FA7E1E]/15 via-[#833AB4]/15 to-[#5851DB]/15"
              disabled={needsTarget || createTarget.isPending || !instagramEnabled}
              disabledHint={!instagramEnabled ? undefined : ''}
              onClick={() => void handleInstagramConnect()}
            />
            {facebookEnabled && (
              <TypeRow
                name="Facebook"
                icon={<FacebookGlyph />}
                iconTintClass="bg-[#0866FF]/10"
                disabled={needsTarget || createTarget.isPending || !fbTargetWs || facebook.step === 'awaitingToken'}
                disabledHint={facebook.step === 'awaitingToken' ? 'Waiting for Facebook' : ''}
                onClick={() => void facebook.start()}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface WorkspacePickerProps {
  kind: 'team' | 'customer';
  options: WorkspaceOption[];
  selected: WorkspaceOption | null;
  /** Org-admin only — see the AIT-521 note in ConnectChannelModal. */
  canCreate: boolean;
  creatingNew: boolean;
  onSelect: (ws: WorkspaceOption | null) => void;
  onCreate: () => void;
}

function WorkspacePicker({
  kind,
  options,
  selected,
  canCreate,
  creatingNew,
  onSelect,
  onCreate,
}: WorkspacePickerProps) {
  const noun = kind === 'customer' ? 'customer' : 'workspace';
  const items = canCreate ? [...options, CREATE_NEW_TARGET] : options;

  return (
    <Combobox.Root
      items={items}
      value={creatingNew ? CREATE_NEW_TARGET : selected}
      onValueChange={(item: WorkspaceOption | null) => {
        if (item?.publicId === CREATE_NEW_TARGET.publicId) onCreate();
        else onSelect(item);
      }}
      filter={(item: WorkspaceOption, query) =>
        item.publicId === CREATE_NEW_TARGET.publicId ||
        item.name.toLowerCase().includes(query.trim().toLowerCase())
      }
      itemToStringLabel={(item: WorkspaceOption | null) => item?.name ?? ''}
      isItemEqualToValue={(a: WorkspaceOption, b: WorkspaceOption) => a?.publicId === b?.publicId}
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Combobox.Input
          aria-label={`Select ${noun}`}
          placeholder={canCreate ? `Search or create ${noun}s...` : `Search ${noun}s...`}
          className="flex h-9 w-full rounded-md border border-input bg-transparent pr-8 pl-8 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Combobox.Trigger
          aria-label={`Open ${noun} list`}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground outline-none hover:text-foreground"
        >
          <ChevronsUpDown className="size-4" />
        </Combobox.Trigger>
      </div>
      <Combobox.Portal>
        <Combobox.Positioner positionMethod="fixed" align="start" side="bottom" sideOffset={4} className="z-50">
          <Combobox.Popup className="w-(--anchor-width) max-h-60 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none">
            <Combobox.List>
              {(item: WorkspaceOption) => (
                <Combobox.Item
                  key={item.publicId}
                  value={item}
                  className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                >
                  {item.publicId === CREATE_NEW_TARGET.publicId ? (
                    <Plus className="size-4" />
                  ) : null}
                  <span className="truncate">{item.name}</span>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}

export function InstagramGlyph() {
  // Instagram's identity is the brand gradient (yellow → magenta → purple), not
  // the flat #E4405F pink, which reads as a danger/error chip next to the
  // WhatsApp green. Same lucide Instagram paths, drawn with a gradient stroke at
  // the shared 24px viewBox for visual parity with the WhatsApp glyph.
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5" fill="none">
      <defs>
        <linearGradient id="ig-gradient" x1="0" y1="24" x2="24" y2="0">
          <stop offset="0" stopColor="#FEDA75" />
          <stop offset="0.3" stopColor="#FA7E1E" />
          <stop offset="0.55" stopColor="#A23B9E" />
          <stop offset="0.8" stopColor="#833AB4" />
          <stop offset="1" stopColor="#5851DB" />
        </linearGradient>
      </defs>
      <g
        stroke="url(#ig-gradient)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
      </g>
    </svg>
  );
}

export function WhatsAppGlyph() {
  // Inline brand glyph — lucide-react omits WhatsApp (trademark policy) so the
  // path comes from Simple Icons' open WhatsApp logo, drawn at the same 24px
  // viewBox as the lucide Instagram icon for visual parity. Filled green.
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-5 text-[#25D366]"
      fill="currentColor"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}
